import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  Bell, 
  MessageCircle, 
  Calendar, 
  DollarSign, 
  Trophy, 
  AlertTriangle,
  Settings,
  CheckCircle,
  X,
  ExternalLink,
  Trash2
} from 'lucide-react'
import { useNotificationStore, Notification } from '@/stores/notificationStore'
import { formatDistanceToNow } from 'date-fns'

interface NotificationDropdownProps {
  isOpen: boolean
  onClose: () => void
}

export function NotificationDropdown({ isOpen, onClose }: NotificationDropdownProps) {
  const navigate = useNavigate()
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    removeNotification,
    clearAllNotifications
  } = useNotificationStore()
  
  const [showAll, setShowAll] = useState(false)

  const getNotificationIcon = (type: Notification['type']) => {
    const iconProps = { className: "w-4 h-4" }
    
    switch (type) {
      case 'client_message':
        return <MessageCircle {...iconProps} className="w-4 h-4 text-blue-500" />
      case 'session_reminder':
        return <Calendar {...iconProps} className="w-4 h-4 text-orange-500" />
      case 'payment_received':
        return <DollarSign {...iconProps} className="w-4 h-4 text-green-500" />
      case 'goal_achieved':
        return <Trophy {...iconProps} className="w-4 h-4 text-yellow-500" />
      case 'check_in_overdue':
        return <AlertTriangle {...iconProps} className="w-4 h-4 text-red-500" />
      case 'workout_completed':
        return <CheckCircle {...iconProps} className="w-4 h-4 text-green-500" />
      case 'system':
        return <Settings {...iconProps} className="w-4 h-4 text-gray-500" />
      default:
        return <Bell {...iconProps} className="w-4 h-4 text-gray-500" />
    }
  }

  const getPriorityColor = (priority: Notification['priority']) => {
    switch (priority) {
      case 'high':
        return 'border-l-red-500'
      case 'medium':
        return 'border-l-yellow-500'
      case 'low':
        return 'border-l-green-500'
      default:
        return 'border-l-gray-300'
    }
  }

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read) {
      markAsRead(notification.id)
    }
    
    if (notification.actionUrl) {
      navigate(notification.actionUrl)
      onClose()
    }
  }

  const handleRemoveNotification = (e: React.MouseEvent, notificationId: string) => {
    e.stopPropagation()
    removeNotification(notificationId)
  }

  const displayedNotifications = showAll ? notifications : notifications.slice(0, 8)

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-40" 
        onClick={onClose}
      />
      
      {/* Dropdown */}
      <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-[600px] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <Bell className="w-5 h-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
            {unreadCount > 0 && (
              <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-600 rounded-full">
                {unreadCount}
              </span>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Mark all read
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600 rounded"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Notifications List */}
        <div className="flex-1 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="p-8 text-center">
              <Bell className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-sm">No notifications yet</p>
            </div>
          ) : (
            <div className="py-2">
              {displayedNotifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`
                    group relative px-4 py-3 border-l-4 cursor-pointer transition-colors
                    ${getPriorityColor(notification.priority)}
                    ${!notification.read 
                      ? 'bg-blue-50 hover:bg-blue-100' 
                      : 'hover:bg-gray-50'
                    }
                  `}
                >
                  <div className="flex items-start space-x-3">
                    {/* Avatar or Icon */}
                    <div className="flex-shrink-0">
                      {notification.avatar ? (
                        <img 
                          src={notification.avatar} 
                          alt={notification.clientName || 'User'} 
                          className="w-8 h-8 rounded-full"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                          {getNotificationIcon(notification.type)}
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <p className={`text-sm ${!notification.read ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'}`}>
                          {notification.title}
                        </p>
                        
                        {/* Actions */}
                        <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          {notification.actionUrl && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleNotificationClick(notification)
                              }}
                              className="p-1 text-gray-400 hover:text-blue-600 rounded"
                              title="View details"
                            >
                              <ExternalLink className="w-3 h-3" />
                            </button>
                          )}
                          <button
                            onClick={(e) => handleRemoveNotification(e, notification.id)}
                            className="p-1 text-gray-400 hover:text-red-600 rounded"
                            title="Remove notification"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                      
                      <p className="text-sm text-gray-600 mt-1 pr-6">
                        {notification.message}
                      </p>
                      
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-gray-500">
                          {formatDistanceToNow(notification.timestamp, { addSuffix: true })}
                        </span>
                        
                        {!notification.read && (
                          <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className="p-3 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              {notifications.length > 8 && (
                <button
                  onClick={() => setShowAll(!showAll)}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  {showAll ? 'Show less' : `View all ${notifications.length} notifications`}
                </button>
              )}
              
              <div className="flex items-center space-x-2 ml-auto">
                <button
                  onClick={() => {
                    clearAllNotifications()
                    onClose()
                  }}
                  className="text-sm text-red-600 hover:text-red-700 font-medium"
                >
                  Clear all
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}